import React, { useEffect, useMemo, useState } from "react";
import AdminAPI from "../../../services/AdminAPI";
import { useACPP } from "../ACPPContext";
import {
  ModuleCard, ModuleHeader, SectionDivider, SoftPanel, InfoBanner,
  MetricCard, Grid4, BtnRow, BtnPrimary, BtnSecondary, BtnLight,
  FormInput, FormSelect, FormTextarea, HelpText,
  EmptyState, TableWrap, Th, Td,
} from "../components/ui";

const MODULE7_KEY = "assessmentStructure";
const ASSESSMENT_TYPES = ["Assessment As Learning", "Assessment Of Learning", "Assessment For Learning"];
const SUB_TYPES = ["Activity Based", "Self", "Peer", "Formative", "Summative", "Portfolio", "Observation", "Written Test", "Oral", "Practical"];
const DIFFICULTY_OPTIONS = ["easy", "medium", "hard", "mixed"];

function isSecondaryStage(grade = "") {
  return ["9", "10", "11", "12"].includes(String(grade || "").trim());
}

function createEmptyOutline(academicYear = "") {
  return {
    rowId: "",
    academicYear,
    grade: "",
    section: "",
    subject: "",
    domain: "",
    title: "",
    duration: "",
    totalMarks: "",
    difficulty: "medium",
    numQuestions: "10",
    tenure: "",
    assessmentType: "",
    assessmentSubTypes: [],
    unit: "",
    chapter: "",
    competencies: [],
    competenciesText: "",
    chaptersCovered: [],
    unitNo: "",
    unitName: "",
  };
}

function toSignature(value) {
  return JSON.stringify(value || []);
}

function dedupe(values = []) {
  const seen = new Set();
  const out = [];
  (values || []).forEach((value) => {
    const clean = String(value || "").trim();
    if (!clean) return;
    const key = clean.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(clean);
  });
  return out;
}

function parseCompetencyText(value = "") {
  return dedupe(
    String(value || "")
      .replace(/\r/g, "\n")
      .replace(/,/g, "\n")
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean)
  );
}

function isOutlineDraftDirty(draft) {
  if (!draft) return false;
  return !![
    draft.grade,
    draft.section,
    draft.subject,
    draft.domain,
    draft.title,
    draft.duration,
    draft.totalMarks,
    draft.difficulty,
    draft.numQuestions,
    draft.tenure,
    draft.assessmentType,
    draft.unit,
    draft.chapter,
    draft.competenciesText,
    ...(Array.isArray(draft.assessmentSubTypes) ? draft.assessmentSubTypes : []),
    ...(Array.isArray(draft.competencies) ? draft.competencies : []),
  ].some((value) => String(value || "").trim());
}

function buildCompetencyCount(row) {
  const list = Array.isArray(row?.competencies) ? row.competencies : [];
  return list.length ? `${list.length} selected` : "—";
}

export default function Module7AssessmentStructure() {
  const {
    canEditModules1to5,
    module5OwnershipMappings,
    setModule7Assessments,
    getActiveAcademicYear,
    getM6FlatRows,
  } = useACPP();

  const academicYear = getActiveAcademicYear();
  const [outlines, setOutlines] = useState([]);
  const [savedSnapshot, setSavedSnapshot] = useState([]);
  const [draft, setDraft] = useState(createEmptyOutline(academicYear));
  const [selectedBaseUid, setSelectedBaseUid] = useState("");
  const [domainOptions, setDomainOptions] = useState({ domains: [], subjects: [] });
  const [competencyOptions, setCompetencyOptions] = useState([]);
  const [customCompetencyInput, setCustomCompetencyInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [actionError, setActionError] = useState("");
  const [executionLoading, setExecutionLoading] = useState(false);
  const [executionRows, setExecutionRows] = useState([]);
  const [teacherCreatedRows, setTeacherCreatedRows] = useState([]);
  const [previewRow, setPreviewRow] = useState(null);
  const [previewCompetencyOptions, setPreviewCompetencyOptions] = useState([]);
  const [previewCompetencyLoading, setPreviewCompetencyLoading] = useState(false);

  const bases = useMemo(() => {
    return (module5OwnershipMappings || [])
      .filter((r) => r.grade && r.section && r.subject)
      .map((r) => ({
        uid: r.uid || `${r.grade}|${r.section}|${r.subject}`,
        label: `${r.grade} — ${r.section} — ${r.subject}`,
        grade: r.grade,
        section: r.section,
        subject: r.subject,
      }));
  }, [module5OwnershipMappings]);

  const m6Rows = useMemo(() => {
    if (typeof getM6FlatRows === "function") return getM6FlatRows() || [];
    return [];
  }, [getM6FlatRows]);

  const unitOptions = useMemo(() => {
    const seen = new Set();
    return m6Rows
      .filter((row) =>
        row.grade === draft.grade &&
        row.section === draft.section &&
        row.subject === draft.subject &&
        row.unitName &&
        row.unitName !== "—"
      )
      .map((row) => ({
        value: `${row.unitNo || ""}__${row.unitName || ""}`,
        label: [row.unitNo, row.unitName].filter((item) => item && item !== "—").join(" • ") || row.unitName,
        unitNo: row.unitNo || "",
        unitName: row.unitName || "",
      }))
      .filter((option) => {
        if (seen.has(option.value)) return false;
        seen.add(option.value);
        return true;
      });
  }, [m6Rows, draft.grade, draft.section, draft.subject]);

  const chapterOptions = useMemo(() => {
    const seen = new Set();
    const selectedUnitKey = `${draft.unitNo || ""}__${draft.unit || draft.unitName || ""}`;
    return m6Rows
      .filter((row) =>
        row.grade === draft.grade &&
        row.section === draft.section &&
        row.subject === draft.subject &&
        row.chapterName &&
        row.chapterName !== "—" &&
        (!selectedUnitKey || selectedUnitKey === "__" || `${row.unitNo || ""}__${row.unitName || ""}` === selectedUnitKey)
      )
      .map((row) => ({
        value: row.chapterName || "",
        label: row.chapterNo && row.chapterNo !== "—" ? `${row.chapterNo}. ${row.chapterName}` : row.chapterName,
      }))
      .filter((option) => {
        if (!option.value || seen.has(option.value)) return false;
        seen.add(option.value);
        return true;
      });
  }, [m6Rows, draft.grade, draft.section, draft.subject, draft.unitNo, draft.unit, draft.unitName]);

  const loadData = async () => {
    setLoading(true);
    setActionError("");
    try {
      const res = await AdminAPI.acppGetModuleData(MODULE7_KEY, academicYear);
      const next = Array.isArray(res?.assessments) ? res.assessments : [];
      setOutlines(next);
      setSavedSnapshot(next);
      setModule7Assessments(next);
      setDraft(createEmptyOutline(academicYear));
      setSelectedBaseUid("");
    } catch (error) {
      const msg = String(error?.message || "");
      if (msg.toLowerCase().includes("404") || msg.toLowerCase().includes("not found")) {
        setOutlines([]);
        setSavedSnapshot([]);
        setModule7Assessments([]);
        setDraft(createEmptyOutline(academicYear));
        setSelectedBaseUid("");
      } else {
        setActionError(msg || "Failed to load Module 7 data.");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadExecution = async () => {
    setExecutionLoading(true);
    try {
      const res = await AdminAPI.acppGetModule7Execution(academicYear);
      setExecutionRows(Array.isArray(res?.rows) ? res.rows : []);
      setTeacherCreatedRows(Array.isArray(res?.teacher_created_unmatched) ? res.teacher_created_unmatched : []);
    } catch (_) {
      setExecutionRows([]);
      setTeacherCreatedRows([]);
    } finally {
      setExecutionLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    loadExecution();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicYear]);

  useEffect(() => {
    if (!draft.grade) {
      setDomainOptions({ domains: [], subjects: [] });
      return;
    }
    let cancelled = false;
    AdminAPI.acppGetLessonPlanDomains(draft.grade)
      .then((res) => {
        if (cancelled) return;
        setDomainOptions({
          domains: Array.isArray(res?.domains) ? res.domains : [],
          subjects: Array.isArray(res?.subjects) ? res.subjects : [],
        });
      })
      .catch(() => {
        if (!cancelled) setDomainOptions({ domains: [], subjects: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [draft.grade]);

  useEffect(() => {
    const isCustomSubject = Array.isArray(domainOptions.subjects) && domainOptions.subjects.some((s) => s.name === draft.domain);
    if (!draft.grade || !draft.domain || isSecondaryStage(draft.grade) || isCustomSubject) {
      setCompetencyOptions([]);
      return;
    }
    let cancelled = false;
    AdminAPI.acppGetLessonPlanCompetencies(draft.grade, draft.domain)
      .then((res) => {
        if (cancelled) return;
        setCompetencyOptions(Array.isArray(res?.competencies) ? res.competencies : []);
      })
      .catch(() => {
        if (!cancelled) setCompetencyOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [draft.grade, draft.domain, domainOptions.subjects]);

  const status = useMemo(() => {
    if (isOutlineDraftDirty(draft)) return "Ready to Append";
    if (!outlines.length && !savedSnapshot.length) return "Pending";
    return toSignature(outlines) === toSignature(savedSnapshot) ? "Saved" : "In Progress";
  }, [draft, outlines, savedSnapshot]);

  const applyBase = (uid) => {
    const base = bases.find((item) => item.uid === uid);
    setSelectedBaseUid(uid);
    if (!base) {
      setDraft((prev) => ({
        ...prev,
        grade: "",
        section: "",
        subject: "",
        domain: "",
        unit: "",
        chapter: "",
        competencies: [],
        competenciesText: "",
      }));
      return;
    }
    setDraft((prev) => ({
      ...prev,
      academicYear,
      grade: base.grade,
      section: base.section,
      subject: base.subject,
      domain: "",
      unit: "",
      unitNo: "",
      unitName: "",
      chapter: "",
      competencies: [],
      competenciesText: "",
      chaptersCovered: [],
    }));
  };

  const setDraftField = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const toggleCompetency = (id) => {
    setDraft((prev) => {
      const current = Array.isArray(prev.competencies) ? prev.competencies : [];
      const exists = current.includes(id);
      return {
        ...prev,
        competencies: exists ? current.filter((item) => item !== id) : [...current, id],
      };
    });
  };

  const resetDraft = () => {
    setDraft(createEmptyOutline(academicYear));
    setSelectedBaseUid("");
    setCompetencyOptions([]);
    setCustomCompetencyInput("");
  };

  const save = async () => {
    setSaving(true);
    setActionError("");
    try {
      if (!draft.grade || !draft.section || !draft.subject || !selectedBaseUid) {
        throw new Error("Please choose a valid Class / Section assignment.");
      }
      if (!draft.domain || !draft.title || !draft.duration || !draft.totalMarks || !draft.difficulty || !draft.numQuestions || !draft.tenure || !draft.assessmentType || !draft.unit || !draft.chapter) {
        throw new Error("Please complete all required assessment outline fields.");
      }
      if (!Array.isArray(draft.assessmentSubTypes) || !draft.assessmentSubTypes.length) {
        throw new Error("Please select at least one assessment sub-type.");
      }

      const _isCustomSubject = Array.isArray(domainOptions.subjects) && domainOptions.subjects.some((s) => s.name === draft.domain);
      const competencies = (isSecondaryStage(draft.grade) && !_isCustomSubject)
        ? parseCompetencyText(draft.competenciesText)
        : dedupe(draft.competencies);
      if (!competencies.length) {
        throw new Error("Please add at least one competency.");
      }

      await AdminAPI.acppSaveModuleData(MODULE7_KEY, academicYear, {
        assessments: [
          {
            ...draft,
            academicYear,
            competencies,
            unitName: draft.unit,
            chaptersCovered: draft.chapter ? [draft.chapter] : [],
          },
        ],
      });
      await loadData();
      await loadExecution();
      resetDraft();
      alert("Assessment outline saved.");
    } catch (error) {
      setActionError(error?.message || "Failed to save Module 7.");
    } finally {
      setSaving(false);
    }
  };

  const resetYear = async () => {
    setSaving(true);
    setActionError("");
    try {
      await AdminAPI.acppDeleteModuleData(MODULE7_KEY, academicYear);
      setOutlines([]);
      setSavedSnapshot([]);
      setModule7Assessments([]);
      resetDraft();
      await loadExecution();
    } catch (error) {
      setActionError(error?.message || "Failed to reset Module 7 data.");
    } finally {
      setSaving(false);
    }
  };

  const autoGenerate = async () => {
    if (!window.confirm(
      "Auto-generate Module 7 assessment outlines from all approved Module 6 plans?\n\n" +
      "This will create one outline skeleton per unit from approved plans. " +
      "Existing outlines are preserved — duplicates are skipped automatically."
    )) return;
    setAutoGenerating(true);
    setActionError("");
    try {
      const result = await AdminAPI.acppAutoGenerateModule7(academicYear);
      await loadData();
      await loadExecution();
      alert(result?.message || `Generated ${result?.generated ?? 0} outline(s).`);
    } catch (error) {
      setActionError(error?.message || "Auto-generation failed. Ensure Module 6 plans are approved.");
    } finally {
      setAutoGenerating(false);
    }
  };

  const openPreview = async (row, source) => {
    const nextRow = { ...row, source };
    setPreviewRow(nextRow);
    setPreviewCompetencyOptions([]);
    if (!nextRow.grade || !nextRow.domain || isSecondaryStage(nextRow.grade)) return;
    setPreviewCompetencyLoading(true);
    try {
      const res = await AdminAPI.acppGetLessonPlanCompetencies(nextRow.grade, nextRow.domain);
      setPreviewCompetencyOptions(Array.isArray(res?.competencies) ? res.competencies : []);
    } catch (_) {
      setPreviewCompetencyOptions([]);
    } finally {
      setPreviewCompetencyLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewRow(null);
    setPreviewCompetencyOptions([]);
    setPreviewCompetencyLoading(false);
  };

  const secondaryGrade = isSecondaryStage(draft.grade);
  const isCustomSubject = Array.isArray(domainOptions.subjects) && domainOptions.subjects.some((s) => s.name === draft.domain);

  return (
    <ModuleCard id="module7">
      <ModuleHeader
        step="Module 7"
        title="Assessment Outline"
        description="Each save appends a new outline for teachers. Existing outlines are preserved."
        showStatus={false}
      />

      <InfoBanner>
        Choose the teacher assignment first, then complete the assessment setup exactly as it should appear in the teacher assessment generator.
      </InfoBanner>

      {!!actionError && (
        <div className="mt-3 rounded-xl border border-[#fecaca] bg-[#fff1f2] text-[#9f1239] px-3 py-2 text-sm">{actionError}</div>
      )}

      <SectionDivider />

      <Grid4>
        <MetricCard label="Saved Outlines" value={outlines.length} meta="Teacher-visible assessment outline entries" />
        <MetricCard label="Planning Bases" value={bases.length} meta="Pulled from Module 5 ownership mapping" />
        <MetricCard label="Academic Year" value={academicYear || "—"} meta="Active ACPP plan year" />
        <MetricCard label="Status" value={status} meta="Append-only year-scoped save state" />
      </Grid4>

      <SectionDivider />

      {canEditModules1to5 ? (
        <SoftPanel title="Create Assessment Outline">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Class / Section Assignment</label>
              <FormSelect value={selectedBaseUid} onChange={(e) => applyBase(e.target.value)}>
                <option value="">Select planning base</option>
                {bases.map((base) => (
                  <option key={base.uid} value={base.uid}>{base.label}</option>
                ))}
              </FormSelect>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Class / Grade</label>
              <FormInput value={draft.grade || ""} readOnly placeholder="Selected from planning base" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Section</label>
              <FormInput value={draft.section || ""} readOnly placeholder="Selected from planning base" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Subject Mapping</label>
              <FormInput value={draft.subject || ""} readOnly placeholder="Selected from planning base" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Domain / Subject</label>
              <FormSelect
                value={draft.domain}
                onChange={(e) => {
                  setDraft((prev) => ({ ...prev, domain: e.target.value, competencies: [], competenciesText: "" }));
                  setCustomCompetencyInput("");
                }}
                disabled={!draft.grade}
              >
                <option value="">{draft.grade ? "Select domain or subject" : "Choose class / section first"}</option>
                {domainOptions.domains.length > 0 && (
                  <optgroup label="── Domains ──">
                    {domainOptions.domains.map((d) => (
                      <option key={d.id} value={d.name}>{d.name}</option>
                    ))}
                  </optgroup>
                )}
                {domainOptions.subjects.length > 0 && (
                  <optgroup label="── Subjects / Classes ──">
                    {domainOptions.subjects.map((s) => (
                      <option key={s.id} value={s.name}>{s.name}</option>
                    ))}
                  </optgroup>
                )}
              </FormSelect>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Assessment Title</label>
              <FormInput value={draft.title} onChange={(e) => setDraftField("title", e.target.value)} placeholder="Enter assessment title" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Tenure</label>
              <FormInput value={draft.tenure} onChange={(e) => setDraftField("tenure", e.target.value)} placeholder="e.g. Weekly / Unit / Term" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Type of Assessment</label>
              <FormSelect value={draft.assessmentType} onChange={(e) => setDraftField("assessmentType", e.target.value)}>
                <option value="">Select type</option>
                {ASSESSMENT_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </FormSelect>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Duration (minutes)</label>
              <FormInput value={draft.duration} onChange={(e) => setDraftField("duration", e.target.value)} placeholder="e.g. 60" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Total Marks</label>
              <FormInput value={draft.totalMarks} onChange={(e) => setDraftField("totalMarks", e.target.value)} placeholder="e.g. 50" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Difficulty</label>
              <FormSelect value={draft.difficulty} onChange={(e) => setDraftField("difficulty", e.target.value)}>
                {DIFFICULTY_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </FormSelect>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Number of Questions</label>
              <FormInput value={draft.numQuestions} onChange={(e) => setDraftField("numQuestions", e.target.value)} placeholder="e.g. 10" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Unit</label>
              <FormSelect
                value={`${draft.unitNo || ""}__${draft.unit || draft.unitName || ""}`}
                onChange={(e) => {
                  const selected = unitOptions.find((item) => item.value === e.target.value);
                  setDraft((prev) => ({
                    ...prev,
                    unit: selected?.unitName || "",
                    unitNo: selected?.unitNo || "",
                    unitName: selected?.unitName || "",
                    chapter: "",
                    chaptersCovered: [],
                  }));
                }}
                disabled={!draft.grade || !unitOptions.length}
              >
                <option value="">{draft.grade ? (unitOptions.length ? "Select unit" : "No units available") : "Choose class / section first"}</option>
                {unitOptions.map((unit) => (
                  <option key={unit.value} value={unit.value}>{unit.label}</option>
                ))}
              </FormSelect>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#1D3469]">Chapter</label>
              <FormSelect
                value={draft.chapter}
                onChange={(e) => setDraft((prev) => ({ ...prev, chapter: e.target.value, chaptersCovered: e.target.value ? [e.target.value] : [] }))}
                disabled={!draft.grade || !chapterOptions.length}
              >
                <option value="">{draft.grade ? (chapterOptions.length ? "Select chapter" : "No chapters available") : "Choose class / section first"}</option>
                {chapterOptions.map((chapter) => (
                  <option key={chapter.value} value={chapter.value}>{chapter.label}</option>
                ))}
              </FormSelect>
            </div>
          </div>

          <div className="mt-6">
            <label className="text-sm font-semibold text-[#1D3469] block mb-3">Assessment Sub-Type(s)</label>
            <select
              multiple
              value={draft.assessmentSubTypes}
              onChange={(e) => setDraftField("assessmentSubTypes", Array.from(e.target.selectedOptions).map((option) => option.value))}
              className="w-full border border-[#cad7e7] rounded-xl bg-white text-[12px] p-3 min-h-[110px] outline-none focus:border-[#1565c0]"
            >
              {SUB_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <HelpText>Hold CTRL (Windows) or CMD (Mac) to select multiple sub-types.</HelpText>
          </div>

          <div className="mt-6">
            <label className="text-sm font-semibold text-[#1D3469] block mb-3">Competencies</label>
            {isCustomSubject ? (
              <div>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={customCompetencyInput}
                    onChange={(e) => setCustomCompetencyInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && customCompetencyInput.trim()) {
                        e.preventDefault();
                        const val = customCompetencyInput.trim();
                        if (!(draft.competencies || []).includes(val)) {
                          setDraft((prev) => ({ ...prev, competencies: [...(prev.competencies || []), val] }));
                        }
                        setCustomCompetencyInput("");
                      }
                    }}
                    className="flex-1 border border-[#cad7e7] rounded-xl px-3 py-2 text-[13px] outline-none focus:border-[#1565c0]"
                    placeholder="Type a competency and press Add or Enter"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const val = customCompetencyInput.trim();
                      if (val && !(draft.competencies || []).includes(val)) {
                        setDraft((prev) => ({ ...prev, competencies: [...(prev.competencies || []), val] }));
                      }
                      setCustomCompetencyInput("");
                    }}
                    disabled={!customCompetencyInput.trim()}
                    className="px-4 py-2 bg-[#1D3469] text-white rounded-xl font-semibold text-[13px] disabled:opacity-40 hover:bg-[#16294f] transition-colors"
                  >
                    Add
                  </button>
                </div>
                {(draft.competencies || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(draft.competencies || []).map((c, i) => (
                      <span key={i} className="inline-flex items-center gap-1 bg-[#1D3469]/10 border border-[#1D3469]/30 text-[#1D3469] text-[12px] px-3 py-1 rounded-full font-medium">
                        {c}
                        <button
                          type="button"
                          onClick={() => setDraft((prev) => ({ ...prev, competencies: (prev.competencies || []).filter((_, idx) => idx !== i) }))}
                          className="ml-1 text-[#1D3469] hover:text-red-500 font-bold leading-none"
                        >×</button>
                      </span>
                    ))}
                  </div>
                )}
                <HelpText>Custom competencies for subject-based outlines.</HelpText>
              </div>
            ) : secondaryGrade ? (
              <>
                <FormTextarea
                  value={draft.competenciesText}
                  onChange={(e) => setDraftField("competenciesText", e.target.value)}
                  placeholder="Enter one competency per line or separate by commas"
                  rows={6}
                />
                <HelpText>Grades 9-12 use free-text competencies, matching the current teacher assessment generator.</HelpText>
              </>
            ) : !draft.grade || !draft.domain ? (
              <HelpText>Select class/grade and domain / subject to load curriculum competencies.</HelpText>
            ) : competencyOptions.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-72 overflow-y-auto border border-[#d9e6fb] rounded-2xl p-4 bg-[#fafcff]">
                {competencyOptions.map((item) => (
                  <label key={item.id} className="flex items-start gap-3 rounded-xl border border-[#e7eef9] bg-white px-3 py-3 text-sm">
                    <input
                      type="checkbox"
                      checked={(draft.competencies || []).includes(item.id)}
                      onChange={() => toggleCompetency(item.id)}
                      className="mt-1 h-4 w-4"
                    />
                    <span>
                      <span className="font-semibold text-[#1e5fb3]">{item.id}</span>
                      {" - "}
                      {item.text}
                      <span className="block text-xs text-[#6b7280] mt-1">Goal: {item.goalId}</span>
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <HelpText>No competencies available for the selected grade and domain.</HelpText>
            )}
          </div>

          <BtnRow>
            <BtnSecondary onClick={save} disabled={saving || loading || autoGenerating}>Save Outline</BtnSecondary>
            <BtnPrimary onClick={autoGenerate} disabled={saving || loading || autoGenerating}>
              {autoGenerating ? "Generating…" : "⚡ Auto-Generate from Module 6"}
            </BtnPrimary>
            <BtnLight onClick={resetDraft} disabled={saving || loading || autoGenerating}>Clear Form</BtnLight>
            <BtnLight onClick={async () => { await loadData(); await loadExecution(); }} disabled={saving || loading || autoGenerating}>Refresh Year Data</BtnLight>
          </BtnRow>
        </SoftPanel>
      ) : (
        <SoftPanel title="Read-only View">
          <HelpText>HOD view is read-only for this module in current rollout.</HelpText>
        </SoftPanel>
      )}

      <SoftPanel title="Outline Execution Progress">
        <p className="text-[13px] text-[#6b7280] leading-[1.7] m-0 mb-3">
          Admin-defined outlines matched against teacher assessment submissions. Shows completion status per outline entry.
        </p>
        {executionLoading ? (
          <EmptyState>Loading execution data…</EmptyState>
        ) : (
          <>
            <TableWrap>
              <thead>
                <tr>
                  <Th>#</Th>
                  <Th>Grade</Th>
                  <Th>Section</Th>
                  <Th>Subject</Th>
                  <Th>Domain</Th>
                  <Th>Title</Th>
                  <Th>Type</Th>
                  <Th>Unit</Th>
                  <Th>Chapter</Th>
                  <Th>Competencies</Th>
                  <Th>Status</Th>
                  <Th>Done Type</Th>
                  <Th>Matched Assessment</Th>
                  <Th>Action</Th>
                </tr>
              </thead>
              <tbody>
                {(executionRows || []).map((row, idx) => (
                  <tr key={row.execution_row_id || row.checklist_id || idx}>
                    <Td>{idx + 1}</Td>
                    <Td>{row.grade || "—"}</Td>
                    <Td>{row.section || "—"}</Td>
                    <Td>{row.subject || "—"}</Td>
                    <Td>{row.domain || "—"}</Td>
                    <Td>{row.title || "—"}</Td>
                    <Td>{row.assessment_type || "—"}</Td>
                    <Td>{`${row.unit_no || ""} ${row.unit_name || ""}`.trim() || row.unit || "—"}</Td>
                    <Td>{row.chapter || (row.chapters_covered || []).join(", ") || "—"}</Td>
                    <Td>{buildCompetencyCount(row)}</Td>
                    <Td>{row.status === "done" ? "Done" : "Pending"}</Td>
                    <Td>{row.done_mode || "pending"}</Td>
                    <Td>{row.matched_assessment_id || "—"}</Td>
                    <Td>
                      <button
                        type="button"
                        onClick={() => openPreview(row, "execution")}
                        className="rounded-lg border border-[#cfe0fb] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#1e5fb3] hover:bg-[#f6f9ff]"
                      >
                        View
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
            {!executionRows.length && <EmptyState>No execution rows yet.</EmptyState>}
          </>
        )}
      </SoftPanel>

      <SoftPanel title="Teacher-Created Assessments">
        <p className="text-[13px] text-[#6b7280] leading-[1.7] m-0 mb-3">
          Assessments submitted by teachers that do not match any admin-defined outline for this year.
        </p>
        {executionLoading ? (
          <EmptyState>Loading…</EmptyState>
        ) : (
          <>
            <TableWrap>
              <thead>
                <tr>
                  <Th>#</Th>
                  <Th>Grade</Th>
                  <Th>Subject</Th>
                  <Th>Domain</Th>
                  <Th>Title</Th>
                  <Th>Type</Th>
                  <Th>Unit</Th>
                  <Th>Chapter</Th>
                  <Th>Competencies</Th>
                  <Th>Status</Th>
                  <Th>Teacher</Th>
                  <Th>Assessment ID</Th>
                  <Th>Action</Th>
                </tr>
              </thead>
              <tbody>
                {(teacherCreatedRows || []).map((row, idx) => (
                  <tr key={row.assessment_id || idx}>
                    <Td>{idx + 1}</Td>
                    <Td>{row.grade || "—"}</Td>
                    <Td>{row.subject || "—"}</Td>
                    <Td>{row.domain || "—"}</Td>
                    <Td>{row.title || "—"}</Td>
                    <Td>{row.assessment_type || "—"}</Td>
                    <Td>{row.unit || "—"}</Td>
                    <Td>{row.chapter || (row.chapters_covered || []).join(", ") || "—"}</Td>
                    <Td>{buildCompetencyCount(row)}</Td>
                    <Td>{row.status || "—"}</Td>
                    <Td>{row.teacher_name || "—"}</Td>
                    <Td>{row.assessment_id || "—"}</Td>
                    <Td>
                      <button
                        type="button"
                        onClick={() => openPreview(row, "teacher-created")}
                        className="rounded-lg border border-[#cfe0fb] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#1e5fb3] hover:bg-[#f6f9ff]"
                      >
                        View
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
            {!teacherCreatedRows.length && <EmptyState>No unmatched teacher-created assessments for this year.</EmptyState>}
          </>
        )}
      </SoftPanel>

      {previewRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(15,23,42,0.45)] p-4">
          <div className="w-full max-w-4xl rounded-3xl bg-white shadow-2xl border border-[#d9e6fb] overflow-hidden">
            <div className="flex items-start justify-between gap-4 border-b border-[#e8eef8] px-6 py-5">
              <div>
                <h3 className="text-2xl font-bold text-[#1D3469]">Assessment Outline Preview</h3>
                <p className="text-sm text-[#64748b] mt-1">
                  {previewRow.source === "execution" ? "Saved execution row" : "Teacher-created assessment"} for Module 7.
                </p>
              </div>
              <button
                type="button"
                onClick={closePreview}
                className="rounded-xl border border-[#d9e6fb] px-3 py-2 text-sm font-semibold text-[#1D3469] hover:bg-[#f8fbff]"
              >
                Close
              </button>
            </div>

            <div className="px-6 py-5 space-y-5 max-h-[75vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><span className="font-semibold text-[#1D3469]">Grade:</span> {previewRow.grade || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Section:</span> {previewRow.section || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Subject:</span> {previewRow.subject || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Domain:</span> {previewRow.domain || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Title:</span> {previewRow.title || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Duration:</span> {previewRow.duration || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Total Marks:</span> {previewRow.total_marks || previewRow.totalMarks || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Difficulty:</span> {previewRow.difficulty || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Questions:</span> {previewRow.num_questions || previewRow.numQuestions || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Tenure:</span> {previewRow.tenure || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Type:</span> {previewRow.assessment_type || previewRow.assessmentType || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Done Type:</span> {previewRow.done_mode || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Unit:</span> {`${previewRow.unit_no || ""} ${previewRow.unit_name || ""}`.trim() || previewRow.unit || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Chapter:</span> {previewRow.chapter || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Matched Assessment:</span> {previewRow.matched_assessment_id || previewRow.assessment_id || "—"}</div>
                <div><span className="font-semibold text-[#1D3469]">Teacher:</span> {previewRow.teacher_name || "—"}</div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-[#1D3469] mb-2">Assessment Scope</h4>
                <div className="rounded-2xl border border-[#e7eef9] bg-[#fafcff] px-4 py-3 text-sm text-[#334155]">
                  <div><span className="font-semibold">Sub-Types:</span> {(previewRow.assessment_sub_types || []).join(", ") || "—"}</div>
                  <div className="mt-2"><span className="font-semibold">Chapters Covered:</span> {(previewRow.chapters_covered || []).join(", ") || previewRow.chapter || "—"}</div>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-[#1D3469] mb-2">Competencies</h4>
                {previewCompetencyLoading ? (
                  <div className="rounded-2xl border border-[#e7eef9] bg-[#fafcff] px-4 py-3 text-sm text-[#64748b]">
                    Loading competency details…
                  </div>
                ) : Array.isArray(previewRow.competencies) && previewRow.competencies.length ? (
                  <div className="rounded-2xl border border-[#e7eef9] bg-[#fafcff] px-4 py-3">
                    <ul className="space-y-2 text-sm text-[#334155]">
                      {previewRow.competencies.map((item, idx) => {
                        const detail = previewCompetencyOptions.find((option) => option.id === item);
                        return (
                          <li key={`${item}-${idx}`} className="leading-6">
                            <span className="font-semibold text-[#1e5fb3]">{item}</span>
                            {detail?.text ? <span>{` - ${detail.text}`}</span> : null}
                            {detail?.goalId ? (
                              <span className="block text-xs text-[#64748b] mt-1">Goal: {detail.goalId}</span>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-[#e7eef9] bg-[#fafcff] px-4 py-3 text-sm text-[#64748b]">
                    No competencies stored for this row.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </ModuleCard>
  );
}
